/**
 * Where do the blocks the editor asked for land?
 *
 * The gesture starts with "+" at a precise spot: whatever route is taken from
 * there — pick a block, have one created, or have one modified before taking it —
 * the editor asked for a block THERE. The turn is asynchronous and the catalogue
 * closes immediately, so that spot has to survive until the definition is ready;
 * otherwise the block appears at the end of the page and the click was for
 * nothing.
 *
 * **Un REGISTRE, pas un emplacement unique.** C'était un slot : armer un second
 * « + » pendant qu'un tour tournait écrasait le premier, et l'éditeur perdait
 * les deux — le bloc en cours atterrissait à la place du suivant, le suivant
 * nulle part. Or c'est le geste naturel dès qu'on remplit une page, et il est
 * indispensable dans une grille, où l'on décrit ses zones l'une après l'autre
 * sans attendre. Chaque demande a donc son entrée, et chaque entrée est
 * APPARIÉE au tour qui doit la remplir.
 *
 * L'appariement se fait par `conversationId`, parce que le « + » ouvre une
 * conversation NEUVE (cf. BuilderChatPanel, BLOCK_TARGET_EVENT) : une demande =
 * une conversation = un placement. Il se fait en deux temps, faute de mieux :
 * au clic sur « + » le tour n'existe pas encore (l'éditeur n'a pas écrit sa
 * demande), l'entrée reste donc SANS conversation jusqu'à l'envoi, où le
 * panneau l'adopte (`claimPendingPlacements`). Une entrée jamais adoptée — on a
 * ouvert le catalogue puis renoncé — est remplacée par la suivante : un seul
 * catalogue est ouvert à la fois.
 *
 * A module-scope registry rather than context state (same shape as
 * `editable/unitRegistry`): the placers are set by whoever opened the catalogue
 * (the page canvas, a column, or a grid for one of its cells) and consumed by
 * the single `builder:registry:invalidate` listener in BuilderCanvas — each host
 * keeps its own placement logic instead of the canvas having to know about grid
 * cells.
 *
 * `type` says WHICH block to place when it is already known (the editor picked
 * one from the catalogue and asked to adjust it). Left out, the placer takes
 * whatever type the turn brought into existence — a creation.
 *
 * Each entry is a one-shot: consuming it clears it, so a later unrelated turn
 * never drops a block somewhere the editor did not ask for.
 */

/** @typedef {{id: number, place: (def: object) => void, type: string|null, spot: object|null, conversationId: string|null, armedAt: number}} Placement */

/** @type {Placement[]} */
let placements = []
let nextId = 1

/**
 * Une demande qui n'a jamais été suivie d'un envoi finit par ne plus vouloir
 * rien dire. On ne peut pas la supprimer sur un signal (rien ne dit « l'éditeur
 * a renoncé »), donc on la laisse mourir de vieillesse au prochain armement.
 * Large exprès : un tour d'assistant peut durer plusieurs minutes, et une
 * entrée oubliée ne coûte qu'une ligne de tableau.
 */
const MAX_AGE_MS = 30 * 60 * 1000

const prune = () => {
  const cutoff = Date.now() - MAX_AGE_MS
  placements = placements.filter(p => p.armedAt > cutoff)
}

/**
 * @param {(def: object) => void} placer — receives the fresh registry entry.
 * @param {{type?: string, spot?: object}} [options]
 *   `type` pins which block this placer expects. `spot` describes WHERE, for the
 *   host that will draw the waiting square — it is never read here, only handed
 *   back: each host knows its own geometry (a page index, a column place, a grid
 *   region) and the placement module has no business interpreting it.
 */
export const setPendingPlacement = (placer, { type, spot = null } = {}) => {
  prune()
  // Une entrée pas encore adoptée n'a pas de tour derrière elle : la remplacer
  // est exactement ce que l'ancien slot faisait, et c'est juste — un seul
  // catalogue est ouvert à la fois. Ce qui ne doit plus disparaître, ce sont les
  // demandes DÉJÀ parties.
  placements = placements.filter(p => p.conversationId !== null)
  if (typeof placer !== 'function') return
  placements.push({
    id: nextId++,
    place: placer,
    type: typeof type === 'string' && type ? type : null,
    spot: spot && typeof spot === 'object' ? spot : null,
    conversationId: null,
    armedAt: Date.now(),
  })
}

export const clearPendingPlacement = () => { placements = [] }

/**
 * Le panneau part avec une demande : les entrées en attente d'un tour sont
 * celles-là. Appelé à l'ENVOI, le seul moment où la conversation qui portera la
 * réponse est connue.
 *
 * @param {string} conversationId
 * @returns {number} combien d'entrées ont été adoptées.
 */
export const claimPendingPlacements = (conversationId) => {
  if (!conversationId) return 0
  let claimed = 0
  for (const p of placements) {
    if (p.conversationId === null) { p.conversationId = conversationId; claimed++ }
  }
  return claimed
}

/**
 * Read the armed placements WITHOUT consuming them.
 *
 * This is what lets each host draw a waiting square where the editor clicked
 * « + » while the turn runs. It must not consume: the placement is still owed to
 * the end of the turn, which is the only thing allowed to spend it.
 *
 * @returns {Placement[]}
 */
export const peekPendingPlacements = () => placements

/**
 * The placement owed to a finished turn, removed from the registry.
 *
 * Nommer la conversation, c'est demander SA demande : un tour qui n'en avait
 * aucune ne pose rien, plutôt que de dépenser celle d'un tour encore en cours —
 * c'était le défaut de l'emplacement unique, et il devient visible dès qu'il y a
 * deux demandes de front.
 *
 * Sans conversation — un tour dont on ne sait pas d'où il vient —, on retombe
 * sur la plus ancienne demande déjà partie : ne rien poser serait pire pour
 * l'éditeur, qui a cliqué « + » et attend son bloc.
 *
 * @param {string|null} conversationId
 * @returns {Placement|null} once.
 */
export const consumePlacementFor = (conversationId) => {
  let at
  if (conversationId) {
    at = placements.findIndex(p => p.conversationId === conversationId)
  }
  else {
    at = placements.findIndex(p => p.conversationId !== null)
    if (at === -1 && placements.length) at = 0
  }
  if (at === -1) return null
  const [placement] = placements.splice(at, 1)
  return placement ?? null
}

/**
 * Les places réservées qu'un hôte doit DESSINER, en ce moment.
 *
 * Trois hôtes posent la même question — le canevas d'une page, une grille, un
 * gabarit en colonnes — et la réponse tient en deux conditions : la demande est
 * partie (elle a un tour), et ce tour travaille encore. Une demande armée mais
 * jamais envoyée n'a rien à montrer ; une demande dont le tour est fini non plus,
 * qu'il ait produit un bloc ou une simple phrase.
 *
 * `match` est ce que l'hôte seul sait dire : « ce spot est-il chez moi ? ».
 *
 * @param {Placement[]} list — ce que rend `peekPendingPlacements()`
 * @param {Set<string>} liveIds — les conversations qui ont un tour en vie
 * @param {(spot: object|null) => boolean} match
 * @returns {object[]} les spots, dans l'ordre où ils ont été demandés
 */
export const reservedSpotsFor = (list, liveIds, match) => (list ?? [])
  .filter(p => p.conversationId && liveIds?.has?.(p.conversationId) && match(p.spot))
  .map(p => p.spot)

/**
 * What a pending placement should drop on the canvas at the end of a turn.
 *
 * Pure, so the rule can be read and tested on its own — it is the whole answer to
 * "I clicked + here, why did nothing land?".
 *
 *  - A placement that NAMES a type (the editor picked a block from the catalogue,
 *    then asked to adjust it) places that block, and only if the turn actually
 *    changed its definition. A plain question leaves the page alone.
 *  - A placement that names none (a creation) places whatever type the turn
 *    brought into existence.
 *
 * "Changed" is measured on `signature` — the stored definition plus the catalogue
 * name — the same yardstick `reloadRegistry` uses to decide which instances
 * remount, so the two can never disagree.
 *
 * @param {object} args
 * @param {string[]} args.types — types the turn reported touching
 * @param {{type: string|null}} args.placement
 * @param {Map<string,string>} args.before — type → signature, before the reload
 * @param {Array<{type: string, signature: string}>} args.fresh — after the reload
 * @returns {Array<object>} the registry entries to place, in order
 */
export const resolvePlacements = ({ types, placement, before, fresh }) => {
  if (!placement || !Array.isArray(fresh)) return []
  const entry = (type) => fresh.find((e) => e.type === type)

  if (placement.type) {
    const def = entry(placement.type)
    if (!def) return []
    const changed = !before.has(placement.type) || before.get(placement.type) !== def.signature
    return changed ? [def] : []
  }

  // Le tour DIT ce qu'il a touché : on s'en tient là, et seuls les types qui
  // n'existaient pas avant lui sont posés (une création ne repose pas un bloc
  // dont il a seulement parlé).
  const named = Array.isArray(types) ? types : []
  if (named.length) return named.filter((type) => !before.has(type)).map(entry).filter(Boolean)

  // Le tour ne nomme RIEN. C'est le cas d'un tour dont personne ne tenait le
  // flux : sa fin est apprise par le sondage, qui rend un état — pas la liste
  // des types touchés. Le rechargement complet du catalogue, lui, la donne par
  // différence : ce qui n'existait pas avant ce tour est ce qu'il a créé.
  //
  // Sans ce repli, enchaîner deux demandes ne posait jamais le bloc de la
  // première : on la lâchait pour écrire la seconde, et plus personne n'était
  // là pour dire ce qu'elle avait fabriqué.
  return fresh.filter((e) => !before.has(e.type))
}
